import React from "react";
import { Box, Tabs, Tab, Chip, Typography } from "@mui/material";

// Tab types for RFQ filtering
export type RFQTab = "NEW" | "DIY" | "QUOTED";

interface TabNavigationProps {
  activeTab: RFQTab;
  counts: {
    NEW: number;
    DIY: number;
    QUOTED: number;
  };
  onChange: (tab: RFQTab) => void;
}

const countChipSx = {
  height: 22,
  minWidth: 22,
  fontWeight: 600,
  fontSize: "0.75rem",
  "& .MuiChip-label": { px: 0.875 },
} as const;

const TabNavigation: React.FC<TabNavigationProps> = ({ activeTab, counts, onChange }) => {
  const handleChange = (_: React.SyntheticEvent, newValue: RFQTab) => {
    onChange(newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: 2,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Tabs
        value={activeTab}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons={false}
        sx={{
          minHeight: 48,
          "& .MuiTab-root": {
            minHeight: 48,
            px: { xs: 1.5, sm: 2.5 },
            fontSize: "0.9375rem",
          },
        }}
      >
        <Tab
          value="NEW"
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span">Active RFQs</Box>
              {counts.NEW > 0 && (
                <Chip label={counts.NEW} size="small" color="primary" sx={countChipSx} />
              )}
            </Box>
          }
        />
        <Tab
          value="DIY"
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span">Manual RFQs</Box>
              {counts.DIY > 0 && (
                <Chip
                  label={counts.DIY}
                  size="small"
                  sx={{ ...countChipSx, bgcolor: "text.secondary", color: "common.white" }}
                />
              )}
            </Box>
          }
        />
        <Tab
          value="QUOTED"
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span">Quoted RFQs</Box>
            </Box>
          }
        />
      </Tabs>

      {/* Show quote count summary when on the QUOTED tab */}
      {activeTab === "QUOTED" && counts.QUOTED > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto", py: 1 }}>
          Total quoted RFQs: {counts.QUOTED}
        </Typography>
      )}
    </Box>
  );
};

export default TabNavigation;
